<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0" >
			<HeatMapChart
				v-if="mountpointsSelected.length > 0"
				:isLoading="isLoading"
				:series="series"
				:categories="categories"
				:colorSettings="COLOR_SETTINGS"
				units="%"
			/>
			<EmptyChartFiltersMessage v-else messageKey="gnss-visibility.mountpointsPlaceholder.baseStation" />
		</Col>
		<Col :span="6" :offset="0">
			<VerticalLayout>
				<Card :bordered="false">
					<VerticalLayout>
						<BaseStationSelect
							v-model="mountpointsSelected"
							:onUpdate="satellitesPerBaseStationGet"
							subNetworkFilter
						/>
						<GnssSelect ref="gnssSelectRef" />

						{{ $t('satellites-per-base-station.periodLabel') }}
						<Select
							class="width-100"
							v-model:value="period"
							:options="periodOptions"
							@change="satellitesPerBaseStationGet"
						/>
					</VerticalLayout>
				</Card>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, computed, watch } from 'vue';

import { Row, Col, Card, Select } from 'ant-design-vue';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import Utils from '@/api/Utils';
import { useSubNetworksStore } from '@/stores/subNetworksStore';

import { getNowDayJs } from '@/components/common/dateUtils';

import VerticalLayout from '@/components/common/VerticalLayout';
import EmptyChartFiltersMessage from '@/components/charts/EmptyChartFiltersMessage';
import HeatMapChart from '@/components/charts/HeatMapChart';
import BaseStationSelect from '@/components/common/controls/BaseStationSelect';
import GnssSelect from '@/components/common/controls/GnssSelect.vue';

const COLOR_SETTINGS = [
	{ maxValue: 1, color: Consts.HEAT_MAP_COLORS.red, label: '0%' },
	{ maxValue: 51, color: Consts.HEAT_MAP_COLORS.yellow, label: '< 50%' },
	{ maxValue: 100, color: Consts.HEAT_MAP_COLORS.green, label: '< 100%' },
	{ maxValue: 101, color: Consts.HEAT_MAP_COLORS.blue, label: '100%' },
];


export default {
	components: {
		Row, Col, Card, Select,
		VerticalLayout, HeatMapChart, EmptyChartFiltersMessage,
		BaseStationSelect, GnssSelect,
	},
	setup() {
		const { apiData, isLoading, doRequest } = Api.useSatellitesPerBaseStationStatsGet();
		const mountpointsSelected = ref([]);
		const subNetworksStore = useSubNetworksStore();

		const gnssSelectRef = ref();

		const periodOptions = [
			{ label: '5 min', value: 5 },
			{ label: '10 min', value: 10 },
			{ label: '15 min', value: 15 },
			{ label: '30 min', value: 30 },
			{ label: '45 min', value: 45 },
			{ label: '1 hour', value: 60 },
			{ label: '2 hours', value: 120 },
			{ label: '3 hours', value: 180 },
		];
		const period = ref(5);

		const satellitesPerBaseStationGet = () => {
			if (mountpointsSelected.value.length > 0 && subNetworksStore.subNetworkSelectedId != null && period.value != null) {
				doRequest({
					end_datetime: getNowDayJs().toISOString(),
					period: period.value,
					sub_network_id: subNetworksStore.subNetworkSelectedId,
					mountpoints: mountpointsSelected.value,
				});
			}
		};

		const categories = computed(() => Utils.sortArray(mountpointsSelected.value));
		const series = computed(() => {
			let result = [];
			let valuesMap = new Map();
			let satellites = new Set();
			const gnssSelectedValue = gnssSelectRef.value != null ? gnssSelectRef.value.selected : [];
			apiData.value.statsItems.forEach(item => {
				if (gnssSelectedValue.length > 0 && !gnssSelectedValue.includes(item.satellite.substring(0, 1))) {
					return;
				}
				valuesMap.set(item.mountpoint + '|' + item.satellite, item.percents);
				satellites.add(item.satellite);
			})
			for (let satellite of Utils.sortArray(Array.from(satellites))) {
				let finalSeriesData = [];
				for (let mountpoint of categories.value) {
					finalSeriesData.push({
						x: mountpoint,
						y: valuesMap.get(mountpoint + '|' + satellite) || 0,
					});
				}
				result.push({
					name: satellite,
					data: finalSeriesData,
				});
			}
			return result;
		});

		watch(() => subNetworksStore.subNetworkSelectedId, () => {
			satellitesPerBaseStationGet();
		});

		return {
			isLoading,
			mountpointsSelected,
			gnssSelectRef,

			periodOptions,
			period,

			categories,
			series,
			COLOR_SETTINGS: ref(COLOR_SETTINGS),

			satellitesPerBaseStationGet,
		};
	},
}
</script>
